import { useState } from "react";
import { HandHeart, Mail, Building2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Reveal, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

function VolunteerForm() {
  const { t } = useLang();
  const v = t.involved.volunteer;
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/volunteers", form);
      setDone(true);
      toast.success(v.toast);
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error(t.involved.errorToast);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="volunteer-success">
        <CheckCircle2 className="w-12 h-12 text-[#16A34A] mx-auto" />
        <p className="mt-4 font-display font-semibold text-lg text-[#0A2540]">{v.successTitle}</p>
        <p className="mt-2 text-[#475569]">{v.successBody}</p>
        <Button variant="ghost" className="mt-4 text-[#16A34A]" onClick={() => setDone(false)}>{v.another}</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="volunteer-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="v-name">{v.name}</Label>
          <Input id="v-name" required value={form.name} onChange={set("name")} data-testid="volunteer-name-input" className="mt-1.5" placeholder={v.namePh} />
        </div>
        <div>
          <Label htmlFor="v-email">{v.email}</Label>
          <Input id="v-email" type="email" required value={form.email} onChange={set("email")} data-testid="volunteer-email-input" className="mt-1.5" placeholder={v.emailPh} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="v-phone">{v.phone}</Label>
          <Input id="v-phone" value={form.phone} onChange={set("phone")} data-testid="volunteer-phone-input" className="mt-1.5" placeholder={v.phonePh} />
        </div>
        <div>
          <Label htmlFor="v-interest">{v.interest}</Label>
          <Input id="v-interest" value={form.interest} onChange={set("interest")} data-testid="volunteer-interest-input" className="mt-1.5" placeholder={v.interestPh} />
        </div>
      </div>
      <div>
        <Label htmlFor="v-message">{v.message}</Label>
        <Textarea id="v-message" value={form.message} onChange={set("message")} data-testid="volunteer-message-input" className="mt-1.5" rows={3} placeholder={v.messagePh} />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-[#16A34A] hover:bg-[#0A2540] text-white font-semibold rounded-full h-12" data-testid="volunteer-submit-button">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : v.submit}
      </Button>
    </form>
  );
}

function NewsletterForm() {
  const { t } = useLang();
  const n = t.involved.newsletter;
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", form);
      toast.success(n.toastOk);
      setForm({ name: "", email: "" });
    } catch (err) {
      if (err?.response?.status === 409) toast.info(n.toastDup);
      else toast.error(t.involved.errorToast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="newsletter-form">
      <div>
        <Label htmlFor="n-name" className="text-white/90">{n.name}</Label>
        <Input id="n-name" value={form.name} onChange={set("name")} data-testid="newsletter-name-input" className="mt-1.5 bg-white/10 border-white/30 text-white placeholder:text-white/50" placeholder={n.namePh} />
      </div>
      <div>
        <Label htmlFor="n-email" className="text-white/90">{n.email}</Label>
        <Input id="n-email" type="email" required value={form.email} onChange={set("email")} data-testid="newsletter-email-input" className="mt-1.5 bg-white/10 border-white/30 text-white placeholder:text-white/50" placeholder={n.emailPh} />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-[#E85D04] hover:bg-[#CC5202] text-white font-semibold rounded-full h-12" data-testid="newsletter-submit-button">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : n.submit}
      </Button>
    </form>
  );
}

function SponsorForm() {
  const { t } = useLang();
  const s = t.involved.sponsor;
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/sponsors", form);
      toast.success(s.toast);
      setForm({ company: "", contact_name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.involved.errorToast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="sponsor-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="s-company">{s.company}</Label>
          <Input id="s-company" required value={form.company} onChange={set("company")} data-testid="sponsor-company-input" className="mt-1.5" placeholder={s.companyPh} />
        </div>
        <div>
          <Label htmlFor="s-contact">{s.contact}</Label>
          <Input id="s-contact" required value={form.contact_name} onChange={set("contact_name")} data-testid="sponsor-contact-input" className="mt-1.5" placeholder={s.contactPh} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="s-email">{s.email}</Label>
          <Input id="s-email" type="email" required value={form.email} onChange={set("email")} data-testid="sponsor-email-input" className="mt-1.5" placeholder={s.emailPh} />
        </div>
        <div>
          <Label htmlFor="s-phone">{s.phone}</Label>
          <Input id="s-phone" value={form.phone} onChange={set("phone")} data-testid="sponsor-phone-input" className="mt-1.5" placeholder={s.phonePh} />
        </div>
      </div>
      <div>
        <Label htmlFor="s-message">{s.message}</Label>
        <Textarea id="s-message" value={form.message} onChange={set("message")} data-testid="sponsor-message-input" className="mt-1.5" rows={3} placeholder={s.messagePh} />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] hover:bg-[#16A34A] text-white font-semibold rounded-full h-12" data-testid="sponsor-submit-button">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : s.submit}
      </Button>
    </form>
  );
}

export default function GetInvolved() {
  const { t } = useLang();
  const g = t.involved;

  return (
    <>
      <section className="relative text-white" data-testid="involved-hero">
        <div className="absolute inset-0">
          <img src={IMAGES.volunteersTent} alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A2540]/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 sm:py-28">
          <Reveal>
            <Overline className="text-[#4ade80]">{g.heroOverline}</Overline>
            <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.05]">{g.heroTitle}</h1>
            <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">{g.heroSub}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="involved-forms">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
          <Reveal className="lg:row-span-2">
            <Card className="h-full bg-white border-[#E2E8F0] rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#16A34A]/10 flex items-center justify-center">
                  <HandHeart className="w-6 h-6 text-[#16A34A]" aria-hidden="true" />
                </div>
                <div>
                  <Overline>{g.volunteer.overline}</Overline>
                  <h2 className="font-display font-bold text-2xl text-[#0A2540]">{g.volunteer.title}</h2>
                </div>
              </div>
              <p className="mt-4 text-[#475569]">{g.volunteer.desc}</p>
              <div className="mt-6"><VolunteerForm /></div>
            </Card>
          </Reveal>

          <Reveal delay={0.1}>
            <Card className="bg-[#0A2540] border-0 rounded-2xl p-8 shadow-sm text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#4ade80]" aria-hidden="true" />
                </div>
                <div>
                  <span className="uppercase tracking-[0.2em] text-sm font-bold text-[#4ade80]">{g.newsletter.overline}</span>
                  <h2 className="font-display font-bold text-2xl">{g.newsletter.title}</h2>
                </div>
              </div>
              <p className="mt-4 text-white/75">{g.newsletter.desc}</p>
              <div className="mt-6"><NewsletterForm /></div>
            </Card>
          </Reveal>

          <Reveal delay={0.15}>
            <Card className="bg-white border-[#E2E8F0] rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#E85D04]/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-[#E85D04]" aria-hidden="true" />
                </div>
                <div>
                  <Overline>{g.sponsor.overline}</Overline>
                  <h2 className="font-display font-bold text-2xl text-[#0A2540]">{g.sponsor.title}</h2>
                </div>
              </div>
              <p className="mt-4 text-[#475569]">{g.sponsor.desc}</p>
              <div className="mt-6"><SponsorForm /></div>
            </Card>
          </Reveal>
        </div>
      </section>
    </>
  );
}
